/* eslint-disable import/prefer-default-export */
import {
  LOGIN_SUCCESS,
  LOG_OUT,
  UPDATE_CUSTOM_WORD_LIST,
  UPDATE_IGNORE_WORD_LIST,
  UPDATE_COMPLIANCE_WORD_LIST,
} from './constants/actionTypes';
import arClient from './util/api_client';
import {
  loadUserFlowAfterAuth,
  resetUserFlowIdentity,
} from './components/common/loadTrackingScripts';

/**
 * Auth middleware - manages session persistence.
 *
 * NOTE: Using localStorage for session storage to enable multi-tab support.
 * Sessions persist across browser close but are cleared on logout.
 */
const authChecker = () => (next) => (action) => {
  if (action.type === LOGIN_SUCCESS) {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 30);

    const fullSession = {
      ...action.session,
      // Persist the real token expiry so initOAuth2 on reload can tell an expired token from a fresh one
      expires_at:
        action.session?.expires_at ??
        (action.session?.expires_in ? Date.now() + action.session.expires_in * 1000 : undefined),
      expiration: currentDate,
      email: action.email,
    };

    // Store session in localStorage for multi-tab support
    localStorage.setItem('session', JSON.stringify(fullSession));
    localStorage.setItem('accountData', JSON.stringify(action.accountData));

    const sessionForUserflow = {
      ...action.session,
      email: action.email,
      workspace: action.session?.workspace ?? action.workspaceData ?? null,
    };
    queueMicrotask(() => {
      loadUserFlowAfterAuth(sessionForUserflow);
    });
  } else if (action.type === LOG_OUT) {
    resetUserFlowIdentity();
    // Clear ALL user-specific data from localStorage
    const userDataKeys = [
      'session',
      'sessionMetadata',
      'parentSession',
      'accountData',
      'subscribeto',
      'plan',
      'personaName',
      'tabPerformance',
      'campaignId',
      'newCampaign',
      'tikTokSparkAds',
      'googleDocsAuth',
      'docusignAuth',
      'tinymce-autosave-draft',
      'editor-autosave',
      'lastViewedCampaign',
      'lastViewedInfluencer',
      'selectedFilters',
    ];

    userDataKeys.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore errors for non-existent keys
      }
    });

    // Also clear sessionStorage for any legacy data
    sessionStorage.clear();

    // Clear OAuth 2.0 tokens from api_client
    if (arClient.clearOAuth2) {
      arClient.clearOAuth2();
    }
  }

  return next(action);
};

const alphabetSorter = () => (next) => (action) => {
  if (
    action.type === UPDATE_CUSTOM_WORD_LIST ||
    action.type === UPDATE_IGNORE_WORD_LIST ||
    action.type === UPDATE_COMPLIANCE_WORD_LIST
  ) {
    const alphabet = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
    ];
    let words = action.wordList.slice();

    words = words.filter((a) => a.length > 0);
    words = words.sort((a, b) => {
      return a.localeCompare(b);
    });
    const result = {};
    alphabet.forEach((letter) => {
      result[letter] = [];
      words.forEach((word) => {
        if (word[0].toUpperCase() === letter) {
          result[letter].push(word);
        }
      });
    });
    // eslint-disable-next-line no-param-reassign
    action.wordList = result;
  }

  return next(action);
};

/**
 * middleware for custom debugging, to see what's going to the store
 * and where actions are triggered from
 */
const debugMiddleware = () => (next) => (action) => {
  // if (action.type === 'FEEDBACK_MEASURE_CLICKED') {

  //   console.trace();
  // }

  return next(action);
};

export { authChecker, alphabetSorter, debugMiddleware };
